import { useState } from "react";
import { toast } from "react-toastify";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type {
    VacationRequest,
    VacationRequestStatus,
} from "@/lib/collections/vacations/types";
import { cancelVacationRequest } from "@/lib/collections/vacations/vacationsService";
import { formatDate } from "@/lib/collections/vacations/vacationBalanceService";

type Props = {
    requests: VacationRequest[];
    onChanged: () => void;
};

const STATUS: Record<
    VacationRequestStatus,
    { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
    pending: { label: "Pendiente", variant: "secondary" },
    approved: { label: "Aprobada", variant: "default" },
    rejected: { label: "Rechazada", variant: "destructive" },
    cancelled: { label: "Cancelada", variant: "outline" },
};

export default function ListRequestVacation({ requests, onChanged }: Props) {
    const [cancellingId, setCancellingId] = useState<string | null>(null);

    const cancel = async (id: string) => {
        setCancellingId(id);
        try {
            await cancelVacationRequest(id);
            toast.success("Solicitud cancelada.");
            onChanged();
        } catch (e) {
            console.error(e);
            toast.error(
                e instanceof Error && e.message === "NOT_PENDING"
                    ? "Esa solicitud ya fue revisada por RRHH y no se puede cancelar."
                    : "No se pudo cancelar la solicitud."
            );
            onChanged();
        } finally {
            setCancellingId(null);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Mis solicitudes</CardTitle>
            </CardHeader>
            <CardContent>
                {requests.length === 0 && (
                    <p className="text-sm text-muted-foreground">Aún no has pedido vacaciones.</p>
                )}

                <ul className="divide-y rounded-lg border">
                    {requests.map((r) => {
                        const status = STATUS[r.status];
                        return (
                            <li key={r.$id} className="flex items-start justify-between gap-3 p-3 sm:p-4">
                                <div className="min-w-0 space-y-1">
                                    <p className="font-medium">
                                        {formatDate(r.startDate)} – {formatDate(r.endDate)}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {r.daysDeducted} días descontados
                                    </p>
                                    {r.reason && (
                                        <p className="wrap-break-word text-sm text-muted-foreground">
                                            {r.reason}
                                        </p>
                                    )}
                                    {r.status === "rejected" && r.rejectionReason && (
                                        <p className="wrap-break-word text-sm text-destructive">
                                            Motivo del rechazo: {r.rejectionReason}
                                        </p>
                                    )}
                                </div>

                                <div className="flex shrink-0 flex-col items-end gap-2">
                                    <Badge variant={status.variant}>{status.label}</Badge>
                                    {r.status === "pending" && r.$id && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={cancellingId === r.$id}
                                            onClick={() => cancel(r.$id!)}
                                        >
                                            {cancellingId === r.$id ? "Cancelando..." : "Cancelar"}
                                        </Button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </CardContent>
        </Card>
    );
}